import {
  ActionIcon,
  Checkbox,
  Group,
  Select,
  Stack,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { IconPlus, IconTrash } from "@tabler/icons-react"
import { FieldConstraint } from "@tfg/db"
import { Button, MultiSelect, Loader, AutoCompleteWithQuery } from "@tfg/ui"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { FindProdSiteFieldDefsByOrgIdResponse } from "@tfg/types/schemas/modules/traceability/dtos/find-prod-site-field-defs-by-org-id.dto"
import {
  UpdateProductionSiteFieldDefinition,
  UpdateProductionSiteFieldDefinitionSchema,
} from "@tfg/types/schemas/modules/traceability/queries/update-production-site-field-definition.dto"

import { queryClient } from "~/backend/queryClient"
import {
  useCreateTextContentMutation,
  useListTextContentsQuery,
} from "~/pages/Admin/MasterLists/CustomFields/hooks"
import { useUpdateProductionSiteFieldDefinitionMutation } from "~/pages/Admin/MasterLists/ProductionSiteFieldDefinitions/hooks/useUpdateProductionSiteFieldDefinitionMutation"

import { useListStandardsQuery } from "../CertificatesTypes/Certiicates/hook"

interface Props {
  organizationId: number
  productionSiteFieldDefinition: FindProdSiteFieldDefsByOrgIdResponse[number]
  close: () => void
  activityNames: string[]
}

export const ProductionSiteFieldDefinitionUpdateForm = ({
  organizationId,
  productionSiteFieldDefinition,
  close,
  activityNames: activities,
}: Props) => {
  const { t } = useTranslation("admin")

  const { mutate: updateTextContent, isLoading: isLoadingTextContent } =
    useCreateTextContentMutation({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["textContents"])
        form.setFieldValue("title", data.originalText)
      },
    })
  const onSuccess = () => {
    form.reset()
    close()
  }
  const { mutate, isLoading } = useUpdateProductionSiteFieldDefinitionMutation(
    {
      id: productionSiteFieldDefinition.id,
    },
    { onSuccess }
  )
  const { data: standards, isLoading: isLoadingStandards } =
    useListStandardsQuery()

  const form = useForm<UpdateProductionSiteFieldDefinition>({
    validate: zodResolver(UpdateProductionSiteFieldDefinitionSchema),
    initialValues: {
      forActivities: productionSiteFieldDefinition.forActivities,
      title: productionSiteFieldDefinition.originalText,
      type: productionSiteFieldDefinition.constraint,
      enumValues: productionSiteFieldDefinition.enumValues,
      enabledForClients: [organizationId],
    },
  })

  const parser = UpdateProductionSiteFieldDefinitionSchema.safeParse(
    form.values
  )

  const errors = parser.success ? {} : parser.error.formErrors.fieldErrors

  return (
    <>
      <form onSubmit={form.onSubmit(() => mutate(form.values))}>
        <Stack>
          <Checkbox
            label={t("prodSiteFieldDefs.labels.allActivities")}
            checked={form.values.forActivities.length === activities.length}
            onChange={(value) => {
              if (value.target.checked) {
                form.setFieldValue("forActivities", activities)
              } else {
                form.setFieldValue("forActivities", [])
              }
            }}
          />
          <MultiSelect
            withAsterisk
            data={activities?.map((activity) => ({
              value: activity,
              label: activity,
            }))}
            label={t("prodSiteFieldDefs.labels.activities")}
            placeholder={t("prodSiteFieldDefs.placeholders.activities")}
            {...form.getInputProps("forActivities")}
          />
          <AutoCompleteWithQuery<AdminResponses.TextContent>
            value={form.values.title}
            onChange={(newValue) => {
              if (newValue) {
                form.setFieldValue("title", newValue)
              }
            }}
            label={t("prodSiteFieldDefs.labels.orginalText")}
            placeholder={t("prodSiteFieldDefs.placeholders.orginalText")}
            onCreate={(query: string) => {
              updateTextContent({ originalText: query })
              form.setFieldValue("title", query)
              return null
            }}
            isLoading={isLoadingTextContent}
            parseOptionFunction={(data) =>
              data?.map((item) => ({
                label: item.originalText,
                value: item.originalText,
              })) || []
            }
            query={useListTextContentsQuery}
            error={errors.title ? errors.title[0] : undefined}
          />
          <Select
            withAsterisk
            data={Object.values(FieldConstraint).map((value) => ({
              value,
              label: customFieldConstraintTranslations(value, t),
            }))}
            label={t("prodSiteFieldDefs.labels.type")}
            placeholder={t("prodSiteFieldDefs.placeholders.type")}
            value={form.values.type}
            onChange={(value) => {
              form.getInputProps("type").onChange(value)
              if (
                form.values.type !== "SELECT" &&
                form.values.type !== "MULTI_SELECT"
              ) {
                form.setFieldValue("enumValues", [])
              }
              form.validateField("type")
            }}
            error={errors.type ? errors.type[0] : undefined}
          />

          {(form.values.type === "SELECT" ||
            form.values.type === "MULTI_SELECT") && (
            <Stack>
              <Title order={5}>{t("customFields.enum_values")}</Title>
              <Group>
                {form.values.enumValues.map((value: string, index: number) => (
                  <AutoCompleteWithQuery<AdminResponses.TextContent>
                    key={index}
                    query={useListTextContentsQuery}
                    withAsterisk
                    label={t("prodSiteFieldDefs.labels.orginalText")}
                    placeholder={t(
                      "prodSiteFieldDefs.placeholders.orginalText"
                    )}
                    isLoading={isLoadingTextContent}
                    parseOptionFunction={(data) =>
                      data?.map((item) => ({
                        label: item.originalText,
                        value: item.originalText,
                      })) || []
                    }
                    value={value}
                    rightSection={
                      <ActionIcon
                        onClick={() => {
                          const newEnumValues = [...form.values.enumValues]
                          newEnumValues.splice(index, 1)
                          form.setFieldValue("enumValues", newEnumValues)
                        }}
                      >
                        <IconTrash />
                      </ActionIcon>
                    }
                    onChange={(value) => {
                      const newEnumValues = [...form.values.enumValues]
                      newEnumValues[index] = value ?? ""
                      form.setFieldValue("enumValues", newEnumValues)
                    }}
                    onCreate={(query) => {
                      const newEnumValues = [...form.values.enumValues]
                      newEnumValues[index] = query
                      form.setFieldValue("enumValues", newEnumValues)
                      return null
                    }}
                    error={errors.title ? errors.title[0] : undefined}
                  />
                ))}
              </Group>
              <Button
                icon={<IconPlus />}
                w={200}
                onClick={() => {
                  form.setValues({
                    ...form.values,
                    enumValues: [...form.values.enumValues, ""],
                  })
                }}
              >
                {t("customFields.add_enum_value")}
              </Button>
            </Stack>
          )}
          {form.values.type === "STANDARD" &&
            (isLoadingStandards ? (
              <Loader />
            ) : (
              <MultiSelect
                value={form.values.enumValues}
                data={standards?.map((standard) => ({
                  label: standard.name,
                  value: standard.name,
                }))}
                label="Standards"
                placeholder="Select standards"
                onChange={(value) => {
                  form.setFieldValue("enumValues", value)
                }}
                searchable
              />
            ))}
          {form.values.type === "NUMBER" && (
            <Group>
              <TextInput
                label={t("customFields.labels.min")}
                placeholder={t("customFields.placeholders.min")}
                {...form.getInputProps("min")}
              />
              <TextInput
                label={t("customFields.labels.max")}
                placeholder={t("customFields.placeholders.max")}
                value={form.values.max ?? ""}
                {...form.getInputProps("max")}
              />
            </Group>
          )}
          <Button disabled={!form.isValid()} type="submit" loading={isLoading}>
            {t("customFields.submit")}
          </Button>
        </Stack>
      </form>
    </>
  )
}

export const customFieldConstraintTranslations = (
  constraint: FieldConstraint,
  t: TFunction<"admin", undefined>
) => {
  switch (constraint) {
    case "STRING":
      return t("customFields.constraints.string")
    case "SELECT":
      return t("customFields.constraints.enum")
    case "NUMBER":
      return t("customFields.constraints.number")
    case "MULTI_SELECT":
      return t("customFields.constraints.multi_enum")
    default:
      return constraint
  }
}
